import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import type { OrganizationDetailInvitedOwnersItem } from '@nucleus-commerce/api-client/platform';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { ServerError } from '@/shared/ui/server-error';

import { useUpdateOwnerInvite } from '../hooks/use-update-owner-invite';
import { ownerEmailSchema, type OwnerEmailValues } from '../schemas/profile-schema';

export interface FixOwnerEmailDialogProps {
  organizationId: string;
  owner: OrganizationDetailInvitedOwnersItem;
}

/**
 * Only while the owner is still invited: the invitation moves to the corrected address and is
 * sent again; links sent to the old address stop working.
 */
export function FixOwnerEmailDialog({ organizationId, owner }: FixOwnerEmailDialogProps) {
  const { t } = useTranslation(['organizations', 'common']);
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" className="self-start">
          {t('organizations:fixEmail.open')}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('organizations:fixEmail.title')}</DialogTitle>
          <DialogDescription>
            {t('organizations:fixEmail.description', { email: owner.email })}
          </DialogDescription>
        </DialogHeader>
        {open ? (
          <FixForm organizationId={organizationId} owner={owner} onDone={() => setOpen(false)} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function FixForm({
  organizationId,
  owner,
  onDone,
}: FixOwnerEmailDialogProps & { onDone: () => void }) {
  const { t } = useTranslation(['organizations', 'common']);
  const update = useUpdateOwnerInvite(organizationId);
  const form = useForm<OwnerEmailValues>({
    resolver: useI18nResolver(ownerEmailSchema),
    defaultValues: { email: owner.email },
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);

  const onSubmit = form.handleSubmit(async ({ email }) => {
    try {
      await update.mutateAsync(email);
      onDone();
    } catch (error) {
      applyFieldErrors(error);
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        {update.isError && !update.error.hasFieldErrors ? (
          <ServerError
            error={update.error}
            message={
              update.error.code === 'PASSWORD_ALREADY_SET'
                ? t('organizations:fixEmail.alreadyAccepted')
                : null
            }
          />
        ) : null}
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('organizations:fields.owner.email')}</FormLabel>
              <FormControl>
                <Input type="email" autoComplete="off" autoFocus {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onDone}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={update.isPending}>
            {update.isPending
              ? t('organizations:fixEmail.sending')
              : t('organizations:fixEmail.submit')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
